import { cn } from '@/shared/utils/cn';

export type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'exception';
export type Size = 'sm' | 'md' | 'lg';

/*
 * Action semantics — the owner's 2026-10-06 UAT UX override uses the approved
 * Fidelen Orange treatment for the ONE primary action. The existing neutral,
 * critical and Deep Plum navigation treatments stay distinct.
 *
 *   primary    the ONE normal workflow action per page or step — New request,
 *              Continue, Submit, Approve. Accessible Orange with white content.
 *   exception  a human MUST act — accessible ORANGE (`--action-exception`)
 *              with white content. Never a routine action.
 *   danger     a destructive or refusing act — Cancel order, Reject, Archive.
 *              R-050's critical treatment: outlined, critical ink. Loud
 *              enough to be read, never the page's most inviting control.
 *
 * A repeated row action (Open, View, Select) stays `secondary`/`ghost`, so a
 * list of N items never renders N bright buttons. Every value below is an
 * R-050 role, never a palette step.
 */
const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-exception text-ink-on-exception hover:bg-exception-hover active:bg-exception-active disabled:bg-disabled disabled:text-ink-disabled',
  secondary:
    'bg-card text-ink ring-1 ring-line hover:bg-hover active:bg-active disabled:text-ink-disabled',
  ghost: 'bg-transparent text-ink hover:bg-hover active:bg-active disabled:text-ink-disabled',
  danger:
    'bg-card text-critical-ink ring-1 ring-critical hover:bg-critical-surface active:bg-critical-surface disabled:text-ink-disabled disabled:ring-line',
  exception:
    'bg-exception text-ink-on-exception hover:bg-exception-hover active:bg-exception-active disabled:bg-disabled disabled:text-ink-disabled',
};

const SIZES: Record<Size, string> = {
  // 44px minimum touch target on the small size: these controls appear in the
  // mobile approval and receiving flows. R-050's 36px control height is a
  // desktop mouse figure and does not shrink this (addendum decision 7).
  sm: 'min-h-11 px-3 text-sm',
  md: 'min-h-11 px-4 text-sm',
  lg: 'min-h-12 px-5 text-base',
};

/** The classes a button of this variant and size carries — shared with `ButtonLink`. */
export function buttonClasses(
  variant: Variant = 'primary',
  size: Size = 'md',
  className?: string,
): string {
  return cn(
    'inline-flex cursor-pointer items-center justify-center gap-2 rounded-md font-medium transition-colors focus-visible:outline-2 focus-visible:outline-focus',
    'disabled:cursor-not-allowed',
    VARIANTS[variant],
    SIZES[size],
    className,
  );
}
